import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { BreakingChangesDetail } from "../DesignUploadStep.types";
import { downloadReport } from "../DesignUploadStep.functions";

interface BreakingChangesDialogProps {
  detail: BreakingChangesDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BreakingChangesDialog({ detail, open, onOpenChange }: BreakingChangesDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Breaking Changes</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-gray-700">{detail.message}</p>

        {detail.structures_removed.length > 0 && (
          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-red-700">
              Removed structures ({detail.structures_removed.length})
            </h3>
            <ul className="space-y-1">
              {detail.structures_removed.map((struct) => (
                <li
                  key={struct.id}
                  className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm"
                >
                  <p className="font-medium">{struct.name}</p>
                  <p className="text-xs text-gray-500">{struct.id}</p>
                </li>
              ))}
            </ul>
          </section>
        )}

        {detail.structures_with_name_changes.length > 0 && (
          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-amber-700">
              ID-changing parameter changes ({detail.structures_with_name_changes.length})
            </h3>
            <ul className="space-y-1">
              {detail.structures_with_name_changes.map((struct) => (
                <li
                  key={struct.old_id}
                  className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm space-y-1"
                >
                  <p className="font-medium">{struct.name}</p>
                  <p className="text-xs text-gray-600">
                    ID: <span className="font-mono">{struct.old_id}</span>
                    {struct.old_id !== struct.new_id && (
                      <>
                        {" "}
                        → <span className="font-mono">{struct.new_id}</span>
                      </>
                    )}
                  </p>
                  {struct.changed_fields.length > 0 && (
                    <p className="text-xs text-gray-500">
                      Changed fields: {struct.changed_fields.join(", ")}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => downloadReport(detail)}>
            <Download className="w-4 h-4" />
            Download report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
